/**
 * Policies for team workspaces; Settings for the Personal workspace.
 *
 * A team admin changes company-wide policy here and pushes it to the field.
 * In Personal, the same web chrome renders the signed-in person's actual
 * mobile-app preferences and writes them back to UserSettings. Everything is
 * either a live control or visibly, explainedly off.
 *
 * ## Where a change goes
 *
 * Nowhere near this process's database. Every control POSTs to the console and
 * is forwarded to `backend/` with the caller's own bearer token
 * (`server/backendGateway.ts`). Team writes carry the server-resolved org and
 * pass `assertOrgAdmin`; personal writes carry no user id at all, so backend
 * can update only the identity proven by that token.
 *
 * ## Three different reasons a control can be off, and they are not the same
 *
 *  1. **You are not an admin.** `canEdit` is false; the server said so from a
 *     live Clerk role lookup. Every control is `disabled`, and the reason is
 *     printed at the top rather than left to be inferred from a page full of
 *     inert switches.
 *  2. **This console has no backend to send to.** `backendConfigured` is
 *     false. Same treatment, different sentence — because it is an operator's
 *     problem, not the user's.
 *  3. **The group is LOCKED.** This is NOT a reason the console's control is
 *     off. A lock is about the FIELD: a locked group's value cannot be
 *     overridden on a phone. An admin can still change it here — that is the
 *     whole point of locking it — so the lock renders as a badge and a
 *     Lock/Unlock button, not as a disabled control.
 *
 * That third distinction is the one most likely to be got wrong, so it is
 * spelled out in the badge's own words ("Managed · locked in the field" /
 * "Field staff may change") and asserted in the browser gate.
 *
 * A NON-ADMIN, though, sees a locked policy as locked AND read-only — (1) and
 * (3) at once — which is the forward-flag Plan 2 recorded. Every control
 * carries `data-readonly` and a real `disabled`, so the assertion is on the
 * DOM the user actually gets rather than on a colour.
 *
 * ## Cut rows stay cut (spec §3.3)
 *
 * No "rules you created" section (it follows the Ask-Automate cut), no
 * audio-retention row, no playback-in-shared-links row, no escalate-to-the-VP
 * row. Those rows are absent from `policyRegistry`. Additional temporarily hidden
 * groups are filtered from the settings surface below.
 * `getPolicies.test.ts` asserts the rendered surface never mentions them.
 */

import { useState } from "react";
import {
  fetchPolicies,
  postMutation,
  type PoliciesResponse,
  type PolicyChildRow,
  type PolicyGroupRow,
  type PolicyValue,
} from "../api";
import { useResource } from "../useResource";
import { workspaceLabel } from "../shell/Shell";
import { C, FONT_MONO } from "../tokens";
import {
  Card,
  ConsoleViewProps,
  FailurePanel,
  LoadingPanel,
  META,
  StaleBanner,
  ViewBody,
  ViewHeader,
} from "../components/chrome";

// ---------------------------------------------------------------------------
// Small chrome
// ---------------------------------------------------------------------------

/** The design's 40×24 switch. `readOnly` renders it flat and inert — and
 *  `disabled` is a real attribute, not a style, so a keyboard or a script
 *  cannot press it either. */
/**
 * The policy toggle.
 *
 * `inherited` is the state that was missing. A child whose `source` is
 * `unset` has nobody's decision behind it — LedAI's registry default is what
 * applies — and rendering that as a solid green "on" told the reader two
 * contradictory things one line apart: a switch that looked deliberately
 * enabled, over a caption reading "Not set · LedAI's default applies".
 *
 * Inherited now renders as a GHOST: the knob sits in the value's position, so
 * the effective setting is still legible at a glance, but the track is muted
 * and outlined rather than filled, so "this is the default" and "somebody
 * turned this on" are not the same picture.
 */
function Switch({
  on,
  readOnly,
  onFlip,
  label,
  inherited,
}: {
  on: boolean;
  readOnly: boolean;
  onFlip: () => void;
  label: string;
  /** True when no one has set this — the value shown is the registry default. */
  inherited?: boolean;
}) {
  return (
    <button
      role="switch"
      aria-checked={on}
      aria-label={label}
      disabled={readOnly}
      data-readonly={readOnly ? "true" : "false"}
      data-inherited={inherited ? "true" : "false"}
      onClick={onFlip}
      style={{
        flexShrink: 0,
        width: 40,
        height: 24,
        borderRadius: 12,
        background: inherited ? "transparent" : on ? C.green : C.line2,
        border: inherited ? `1.5px dashed ${C.line2}` : "none",
        position: "relative",
        opacity: readOnly ? 0.5 : 1,
        cursor: readOnly ? "not-allowed" : "pointer",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: on ? 19 : 3,
          width: 18,
          height: 18,
          borderRadius: 9,
          background: inherited ? C.line2 : C.surface,
          boxShadow: inherited ? "none" : `0 1px 3px ${C.scrim}`,
        }}
      />
    </button>
  );
}

function ChoiceChip({
  label,
  active,
  readOnly,
  onPick,
}: {
  label: string;
  active: boolean;
  readOnly: boolean;
  onPick: () => void;
}) {
  return (
    <button
      aria-pressed={active}
      disabled={readOnly}
      data-readonly={readOnly ? "true" : "false"}
      onClick={onPick}
      style={{
        fontSize: 13.8,
        fontWeight: 600,
        padding: "6px 12px",
        borderRadius: 8,
        background: active ? C.ink : C.surface,
        color: active ? C.surface : C.ink,
        border: `1px solid ${active ? C.ink : C.line2}`,
        opacity: readOnly ? 0.55 : 1,
        cursor: readOnly ? "not-allowed" : "pointer",
      }}
    >
      {label}
    </button>
  );
}

function CheckChip({
  label,
  active,
  readOnly,
  onToggle,
}: {
  label: string;
  active: boolean;
  readOnly: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      aria-pressed={active}
      disabled={readOnly}
      data-readonly={readOnly ? "true" : "false"}
      onClick={onToggle}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13.8,
        fontWeight: 600,
        padding: "6px 11px 6px 8px",
        borderRadius: 8,
        background: active ? C.greenSoft : C.surface,
        color: C.ink,
        border: `1px solid ${active ? C.green : C.line2}`,
        opacity: readOnly ? 0.55 : 1,
        cursor: readOnly ? "not-allowed" : "pointer",
      }}
    >
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
        <path
          d="M5 12l5 5L20 6"
          stroke={active ? C.green : C.line2}
          strokeWidth="3.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
      {label}
    </button>
  );
}

function StepperButton({
  glyph,
  readOnly,
  onPress,
  label,
}: {
  glyph: string;
  readOnly: boolean;
  onPress: () => void;
  label: string;
}) {
  return (
    <button
      aria-label={label}
      disabled={readOnly}
      data-readonly={readOnly ? "true" : "false"}
      onClick={onPress}
      style={{
        width: 26,
        height: 26,
        borderRadius: 8,
        border: `1px solid ${C.line2}`,
        background: C.surface,
        fontSize: 17.25,
        fontWeight: 600,
        lineHeight: 1,
        opacity: readOnly ? 0.5 : 1,
        cursor: readOnly ? "not-allowed" : "pointer",
      }}
    >
      {glyph}
    </button>
  );
}

function LockBadge({ locked }: { locked: boolean }) {
  const ink = locked ? C.ink : C.sub;
  return (
    <span
      data-testid="lock-badge"
      data-locked={locked ? "true" : "false"}
      style={{
        ...META,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 9.2,
        letterSpacing: "0.6px",
        color: ink,
        background: locked ? C.faint : "transparent",
        padding: "3px 7px",
        borderRadius: 5,
      }}
    >
      <svg width="9" height="9" viewBox="0 0 16 16" fill="none">
        <rect x="3" y="7" width="10" height="7" rx="1.6" stroke={ink} strokeWidth="1.6" />
        <path d="M5.5 7V5a2.5 2.5 0 015 0v2" stroke={ink} strokeWidth="1.6" />
      </svg>
      {locked ? "Managed · locked in the field" : "Field staff may change"}
    </span>
  );
}

/** Where the value in force came from, in the reader's words. `unset` is
 *  worth saying out loud: it means nobody has decided, which is different
 *  from somebody having decided on the same number. */
const SOURCE_NOTE: Record<PolicyChildRow["source"], string> = {
  locked: "Set by your company · the field cannot change it",
  "policy-default": "Set by your company · the field may still override it",
  user: "Set by the individual",
  unset: "Not set · LedAI's default applies",
};

// ---------------------------------------------------------------------------
// One child row
// ---------------------------------------------------------------------------

function displayValue(child: PolicyChildRow): string {
  const value = child.value;
  if (Array.isArray(value)) return value.length ? value.join(", ") : "None";
  if (typeof value === "boolean") return value ? "On" : "Off";
  if (value === undefined || value === "") return "—";
  return String(value);
}

function ChildRow({
  child,
  readOnly,
  last,
  onSet,
  personal,
}: {
  child: PolicyChildRow;
  readOnly: boolean;
  last: boolean;
  onSet: (value: PolicyValue) => void;
  personal: boolean;
}) {
  const checks = Array.isArray(child.value) ? child.value : [];
  const optionLabel = (option: string) => child.optionLabels?.[option] ?? option;

  return (
    <div
      data-testid="policy-child"
      data-policy-key={child.policyKey}
      data-source={child.source}
      data-readonly={readOnly ? "true" : "false"}
      style={{ padding: "11px 0", borderBottom: `1px solid ${last ? "transparent" : C.line}` }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ fontSize: 15.52, fontWeight: 600 }}>{child.label}</span>
          <span
            style={{ ...META, display: "block", fontSize: 9.77, color: C.sub2, marginTop: 3, letterSpacing: "0.5px" }}
          >
            {child.unavailableReason ??
              (personal ? "Synced with Settings in the mobile app" : SOURCE_NOTE[child.source])}
            {/* An unenforced key is one the console can set, version and
                report as adopted while nothing in the field actually reads
                it. Saying so beats a policy that looks applied and is not. */}
            {!personal && !child.enforced && " · not yet enforced in the field"}
          </span>
        </span>

        {child.type === "toggle" && (
          <Switch
            on={child.value === true}
            readOnly={readOnly}
            label={child.label}
            inherited={child.source === "unset"}
            onFlip={() => onSet(child.value !== true)}
          />
        )}

        {child.type === "info" && (
          <span
            style={{ fontFamily: FONT_MONO, fontSize: 12.08, color: C.sub, letterSpacing: "0.3px", flexShrink: 0 }}
          >
            {displayValue(child)}
          </span>
        )}

        {child.type === "stepper" && (
          <span style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
            <StepperButton
              glyph="−"
              label={`Decrease ${child.label}`}
              readOnly={readOnly}
              onPress={() => onSet(Math.max(0, Number(child.value ?? 0) - 1))}
            />
            <span
              data-testid="stepper-value"
              style={{ fontFamily: FONT_MONO, fontSize: 14.95, fontWeight: 700, minWidth: 22, textAlign: "center" }}
            >
              {displayValue(child)}
            </span>
            <StepperButton
              glyph="+"
              label={`Increase ${child.label}`}
              readOnly={readOnly}
              onPress={() => onSet(Number(child.value ?? 0) + 1)}
            />
          </span>
        )}
      </div>

      {child.type === "choice" && (
        <div style={{ display: "flex", gap: 6, marginTop: 9, flexWrap: "wrap" }}>
          {(child.options ?? []).map((option) => (
            <ChoiceChip
              key={option}
              label={optionLabel(option)}
              active={child.value === option}
              readOnly={readOnly}
              onPick={() => onSet(option)}
            />
          ))}
        </div>
      )}

      {child.type === "checks" && (
        <div style={{ display: "flex", gap: 7, marginTop: 9, flexWrap: "wrap" }}>
          {(child.options ?? []).map((option) => {
            const active = checks.includes(option);
            return (
              <CheckChip
                key={option}
                label={optionLabel(option)}
                active={active}
                readOnly={readOnly}
                onToggle={() =>
                  onSet(active ? checks.filter((value) => value !== option) : [...checks, option])
                }
              />
            );
          })}
        </div>
      )}

      {child.type === "text" && (
        personal ? (
          <textarea
            key={typeof child.value === "string" ? child.value : ""}
            data-testid="personal-setting-text"
            aria-label={child.label}
            defaultValue={typeof child.value === "string" ? child.value : ""}
            disabled={readOnly}
            data-readonly={readOnly ? "true" : "false"}
            rows={4}
            placeholder="Add guidance for how Led AI should interpret and write about your field work."
            onBlur={(event) => {
              const next = event.currentTarget.value.trim();
              const current = typeof child.value === "string" ? child.value.trim() : "";
              if (next !== current) onSet(next);
            }}
            style={{
              width: "100%",
              resize: "vertical",
              marginTop: 9,
              background: C.sunk,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: "11px 13px",
              fontSize: 14.37,
              lineHeight: 1.55,
              color: C.ink,
              minHeight: 82,
              opacity: readOnly ? 0.55 : 1,
            }}
          />
        ) : (
          <div
            style={{
              marginTop: 9,
              background: C.sunk,
              border: `1px solid ${C.line}`,
              borderRadius: 10,
              padding: "11px 13px",
              fontSize: 14.37,
              lineHeight: 1.55,
              color: child.value ? C.ink : C.sub2,
              minHeight: 40,
            }}
          >
            {/* Free text is edited on the device that owns it. The console
                displays what the company set and offers no textarea: a
                half-working editor here would be the only control on the page
                that could lose what you typed. */}
            {child.value ? String(child.value) : "No company instructions set."}
          </div>
        )
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// The view
// ---------------------------------------------------------------------------

export function Policies({ search, refreshKey, personalHref, onGoPersonal }: ConsoleViewProps) {
  /** Bumped after every landed mutation, so the page re-reads the SERVER's
   *  state rather than trusting an optimistic local guess. A policy screen
   *  that showed what you clicked instead of what was stored would be the one
   *  screen where that matters most. */
  const [mutations, setMutations] = useState(0);
  const [busy, setBusy] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const { data, failure, refreshFailure } = useResource<PoliciesResponse>(
    `policies:${search}`,
    () => fetchPolicies(search),
    refreshKey + mutations,
  );

  async function send(path: string, body: Record<string, unknown>, what: string) {
    setBusy(what);
    setProblem(null);
    const result = await postMutation(path, search, body);
    setBusy(null);
    if (!result.ok) {
      setProblem(result.error ?? `That change was refused (${result.status}).`);
      return;
    }
    setMutations((count) => count + 1);
  }

  if (failure) {
    return (
      <FailurePanel failure={failure} personalHref={personalHref} onGoPersonal={onGoPersonal} />
    );
  }
  if (!data) return <LoadingPanel shape={new URLSearchParams(search).get("workspace")?.startsWith("team:") ? "policies" : "settings"} />;

  const personal = data.surface === "personal-settings";
  // Temporarily hide these controls across every project scope. Stored settings
  // and backend enforcement remain unchanged.
  const hiddenGroups = new Set(["loc", "personal-location", "proc", "offline", "speaker", "stall"]);
  const visibleGroups = data.groups
    .filter((group) => !hiddenGroups.has(group.id))
    .map((group) => ({ ...group, children: group.children.filter((child) => child.key !== "reviewStage") }))
    .filter((group) => group.children.length > 0);
  const childCount = visibleGroups.reduce((count, group) => count + group.children.length, 0);
  const lockedGroupCount = visibleGroups.filter((group) => group.locked).length;

  // The one predicate the whole page hangs off. `locked` is deliberately NOT
  // part of it — see the file header.
  const readOnly = !data.backendConfigured || (!personal && !data.canEdit);
  // BOTH reasons, when both apply. They are different problems with different
  // owners — "switch workspace" is the reader's to act on, "configure the
  // backend" is an operator's — and showing only one leaves whoever could fix
  // the other with no idea it is there.
  const readOnlyReason = [
    data.readOnlyReason,
    data.backendConfigured
      ? undefined
      : "This console is not configured to reach the LedAI backend, so nothing here can be changed from this deployment.",
  ]
    .filter(Boolean)
    .join(" ");

  const setValue = (
    groupId: string,
    child: PolicyChildRow,
    value: PolicyValue,
  ) => {
    if (personal) {
      if (!child.settingKey) {
        setProblem(`This setting is missing its mobile-app field mapping (${child.policyKey}).`);
        return;
      }
      const storedValue =
        child.settingKey === "aiPreferenceMode"
          ? value === true
            ? "guided"
            : "auto"
          : value;
      void send(
        "/api/personal-settings/values",
        { changes: [{ key: child.settingKey, value: storedValue }] },
        child.settingKey,
      );
      return;
    }
    void send(
      "/api/policies/values",
      { changes: [{ groupId, childKey: child.key, value }] },
      `${groupId}.${child.key}`,
    );
  };

  const setLock = (group: PolicyGroupRow) =>
    send("/api/policies/lock", { groupId: group.id, locked: !group.locked }, `lock:${group.id}`);

  // Section headings, in the registry's own order, without re-listing them.
  const sections: Array<{ heading: string; groups: PolicyGroupRow[] }> = [];
  for (const group of visibleGroups) {
    const last = sections[sections.length - 1];
    if (last && last.heading === group.group) last.groups.push(group);
    else sections.push({ heading: group.group, groups: [group] });
  }

  return (
    <>
      <ViewHeader
        crumb={personal
          ? `${childCount} settings · synced with mobile`
          : `${lockedGroupCount} of ${visibleGroups.length} policy groups locked · ${childCount} settings`}
        title={personal ? "Settings" : "Policies"}
      >
        {!personal && <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 9,
            marginTop: -34,
          }}
        >
          {data.unpushedChanges && (
            <span
              data-testid="unpushed-chip"
              style={{ ...META, fontSize: 10.93, letterSpacing: "0.6px", color: C.amber }}
            >
              Unpushed changes
            </span>
          )}
          <button
            data-testid="push-policy"
            // Nothing to push is not an action. Left enabled, "Up to date"
            // reads as a button that does nothing when clicked — the same
            // complaint the inert filter chips earned on the Item list.
            disabled={readOnly || busy !== null || !data.unpushedChanges}
            data-readonly={readOnly ? "true" : "false"}
            onClick={() => send("/api/policies/push", {}, "push")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 7,
              height: 36,
              padding: "0 14px",
              borderRadius: 10,
              background: data.unpushedChanges && !readOnly ? C.gold : C.surface,
              border: `1px solid ${data.unpushedChanges && !readOnly ? C.gold : C.line2}`,
              color: data.unpushedChanges && !readOnly ? C.onGold : C.sub,
              fontSize: 14.37,
              fontWeight: 700,
              opacity: readOnly ? 0.5 : 1,
              cursor:
                readOnly || !data.unpushedChanges ? "default" : "pointer",
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 19V5" />
              <path d="m5 12 7-7 7 7" />
            </svg>
            {busy === "push"
              ? "Pushing…"
              : data.unpushedChanges
                ? "Push to all devices"
                : "Up to date"}
          </button>
        </div>}

        {!personal && <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
            background: C.faint,
            borderRadius: 14,
            padding: "14px 16px",
            marginTop: 16,
          }}
        >
          <span
            style={{
              width: 34,
              height: 34,
              borderRadius: 10,
              background: C.surface,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <svg
              width="17"
              height="17"
              viewBox="0 0 24 24"
              fill="none"
              stroke={C.ink}
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 3l8 3v6c0 4.4-3.2 7.6-8 9-4.8-1.4-8-4.6-8-9V6l8-3Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: "block", fontSize: 16.1, fontWeight: 700, letterSpacing: "-0.25px" }}>
              {personal
                ? "Your settings, wherever you use Led AI"
                : `These settings are managed by ${workspaceLabel(data.activeWorkspace)}`}
            </span>
            <span style={{ display: "block", fontSize: 14.37, color: C.sub, marginTop: 4, lineHeight: 1.5 }}>
              {personal
                ? "Changes made here update the same preferences used by the mobile app. Changes made on your phone appear here too."
                : data.unavailableReason ??
                  "Set once here and pushed to every phone and pair of glasses in the field. A locked group is read-only on site — superintendents cannot turn it off there."}
            </span>
            {readOnlyReason.length > 0 && (
              <span
                data-testid="policy-readonly-reason"
                style={{ display: "block", fontSize: 14.37, color: C.amber, marginTop: 6, lineHeight: 1.5 }}
              >
                {readOnlyReason}
              </span>
            )}
          </span>
        </div>}
      </ViewHeader>

      <ViewBody>
        {refreshFailure && <StaleBanner failure={refreshFailure} />}

        {problem && (
          <div
            data-testid="policy-error"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              padding: "10px 13px",
              borderRadius: 10,
              background: C.redSoft,
              border: `1px solid ${C.line}`,
              marginBottom: 14,
              fontSize: 14.37,
              color: C.ink,
            }}
          >
            <span style={{ width: 6, height: 6, borderRadius: 3, background: C.red, flexShrink: 0 }} />
            {problem}
          </div>
        )}

        {sections.map((section) => (
          <div key={section.heading} style={{ marginBottom: 22 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <span style={{ ...META, fontSize: 10.93, letterSpacing: "1.2px", color: C.ink }}>
                {section.heading}
              </span>
              <span style={{ flex: 1, height: 1, background: C.line }} />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {section.groups.map((group) => (
                <Card
                  key={group.id}
                  testId="policy-group"
                  style={{ padding: "16px 18px", borderRadius: 16 }}
                >
                  <div
                    data-policy-group={group.id}
                    data-locked={group.locked ? "true" : "false"}
                    style={{ display: "flex", alignItems: "flex-start", gap: 12 }}
                  >
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 17.25, fontWeight: 700, letterSpacing: "-0.3px" }}>
                          {group.name}
                        </span>
                        {!personal && <LockBadge locked={group.locked} />}
                      </span>
                      <span style={{ display: "block", fontSize: 14.37, color: C.sub, marginTop: 5, lineHeight: 1.5 }}>
                        {group.summary}
                      </span>
                    </span>
                    {!personal && <button
                      data-testid="toggle-lock"
                      disabled={readOnly || busy !== null}
                      data-readonly={readOnly ? "true" : "false"}
                      onClick={() => setLock(group)}
                      style={{
                        ...META,
                        flexShrink: 0,
                        fontSize: 10.35,
                        letterSpacing: "0.6px",
                        color: C.sub,
                        background: C.faint,
                        padding: "6px 10px",
                        borderRadius: 7,
                        opacity: readOnly ? 0.5 : 1,
                        cursor: readOnly ? "not-allowed" : "pointer",
                      }}
                    >
                      {busy === `lock:${group.id}` ? "Saving…" : group.locked ? "Unlock" : "Lock"}
                    </button>}
                  </div>

                  <div style={{ marginTop: 14, paddingTop: 4, borderTop: `1px solid ${C.line}` }}>
                    {group.children.map((child, index) => (
                      <ChildRow
                        key={child.policyKey}
                        child={child}
                        readOnly={readOnly || busy !== null || Boolean(child.unavailableReason)}
                        last={index === group.children.length - 1}
                        personal={personal}
                        onSet={(value) => setValue(group.id, child, value)}
                      />
                    ))}
                  </div>
                </Card>
              ))}
            </div>
          </div>
        ))}

      </ViewBody>
    </>
  );
}
